import React, { Fragment } from "react";
import { Helmet } from "react-helmet";
import { Script } from "gatsby";
import StructuredDataHowToStep from "types/fields/matrix/seo/structuredDataHowToStep";
import StructuredDataHowToStepWithDirection from "types/fields/matrix/seo/structuredDataHowToStepWithDirection";
import HowToStructuredDataStep from "types/seo/howToStructuredDataStep";
import HowToStructuredDataStepWithDirection from "types/seo/howToStructuredDataStepWithDirection";
import MetaDataProps from "types/seo/metaData";
import getJobPostings from "./jobPosting";
import FAQStructuredDataEntity from "types/seo/FAQStructuredDataEntity";

const renderSiteLinksSearchBox = () => {
  return (
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        url: "https://www.delasign.com/",
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate:
              "https://www.delasign.com/search/?q={search_term_string}",
          },
          "query-input": "required name=search_term_string",
        },
      })}
    </script>
  );
};

const MetaData = ({
  seo,
  structuredDataFAQ,
  howToStructuredDataSet,
  noFollow,
  isArticle,
  jobPostings,
  iOSBanner,
}: MetaDataProps) => {
  const { author, path, seoImage, metaTitle, metaDescription } = seo;

  var pageAuthor = author;

  if (author === null) {
    pageAuthor = {
      name: "Oscar de la Hera Gomez",
      linkedinUrl: "https://www.linkedin.com/in/delasign",
      headshot: [
        {
          url: "/oscar-profile-thumbnail.webp",
        },
      ],
    };
  }


  var canonical = process.env.GATSBY_BASE_URL + path + "/"

  if (path === "__home__") {
    canonical = process.env.GATSBY_BASE_URL as string
  } else if (path === "muralize-terms") {
    canonical = process.env.GATSBY_BASE_URL + "muralize/terms/"
  }

  // console.log("-------");
  // console.log("PATH:  ", path);
  // console.log("CANONICAL:  ", canonical);

  const renderStructuredDataFAQ = () => {
    if (structuredDataFAQ && structuredDataFAQ.length > 0) {
      // console.log("STRUCTUED FAQ");

      var FAQs: Array<FAQStructuredDataEntity> = [];
      structuredDataFAQ.forEach((item, _) => {
        FAQs.push({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        });
      });
      return (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQs,
          })}
        </script>
      );
    } else {
      return "";
    }
  };

  const renderStructuredHowTo = () => {
    if (
      howToStructuredDataSet &&
      howToStructuredDataSet.integrateStructuredDataHowTo &&
      howToStructuredDataSet.structuredDataHowToQuestion &&
      howToStructuredDataSet.structuredDataHowToLeadImage &&
      howToStructuredDataSet.structuredDataHowTo &&
      howToStructuredDataSet.structuredDataHowTo.length > 0
    ) {
      // console.log("Structured Data How To exists");
      var steps: Array<
        HowToStructuredDataStep | HowToStructuredDataStepWithDirection
      > = [];

      howToStructuredDataSet.structuredDataHowTo.forEach((item, _) => {
        // console.log("Current How To Step :", item);
        if (item.typeHandle) {
          if (item.typeHandle === "howToStep") {
            const step = item as StructuredDataHowToStep;
            const url = item.directUrl;
            if (item.image && item.image[0] !== undefined) {
              const stepHero = item.image[0].imageAsset[0];
              // console.log("Step Hero : ", stepHero);

              steps.push({
                "@type": "HowToStep",
                name: step.step,
                text: step.text,
                image: {
                  "@type": "ImageObject",
                  url: stepHero.url,
                  height: stepHero.height.toString(),
                  width: stepHero.width.toString(),
                },
                url: url,
              });
            } else {
              steps.push({
                "@type": "HowToStep",
                name: step.step,
                text: step.text,
                url: url,
              });
            }
          } else {
            const stepWithDirection =
              item as StructuredDataHowToStepWithDirection;
            const url = item.directUrl;

            var directions: Array<{
              "@type": "HowToDirection";
              text: string;
            }> = [];

            stepWithDirection.itemListElement.forEach((item, _) => {
              directions.push({
                "@type": "HowToDirection",
                text: item.text,
              });
            });

            if (item.image && item.image[0] !== undefined) {
              const stepWidthDirectionHero = item.image[0].imageAsset[0];
              // console.log("stepWidthDirectionHero : ", stepWidthDirectionHero);
              steps.push({
                "@type": "HowToStep",
                name: stepWithDirection.step,
                itemListElement: directions,
                image: {
                  "@type": "ImageObject",
                  url: stepWidthDirectionHero.url,
                  height: stepWidthDirectionHero.height.toString(),
                  width: stepWidthDirectionHero.width.toString(),
                },
                url: url,
              });
            } else {
              steps.push({
                "@type": "HowToStep",
                name: stepWithDirection.step,
                itemListElement: directions,
                url: url,
              });
            }
          }
        }
      });

      if (
        howToStructuredDataSet.structuredDataHowToLeadImage &&
        howToStructuredDataSet.structuredDataHowToLeadImage[0] !== undefined
      ) {
        const hero =
          howToStructuredDataSet.structuredDataHowToLeadImage[0].imageAsset[0];
        return (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              name: howToStructuredDataSet.structuredDataHowToQuestion,
              image: {
                "@type": "ImageObject",
                url: hero.url,
                height: hero.height.toString(),
                width: hero.width.toString(),
              },
              step: steps,
            })}
          </script>
        );
      } else {
        return (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              name: howToStructuredDataSet.structuredDataHowToQuestion,
              step: steps,
            })}
          </script>
        );
      }
    } else {
      // console.log("Structured Data How To Does Not exist");
      return "";
    }
  };

  const renderStructuredDataJobPostings = () => {
    if (jobPostings) {
      return getJobPostings(
        jobPostings.careerPaths,
        jobPostings.performanceLevels
      );
    } else {
      return "";
    }
  };

  const renderArticleStructuredData = () => {
    if (isArticle) {
      return (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": "https://google.com/article",
            },
            headline: seo.metaTitle,
            image: [seo.seoImage.url],
            datePublished: seo.dateCreated,
            dateModified: seo.dateUpdated,
            author: {
              "@type": "Person",
              name: author.name,
              url: "https://www.linkedin.com/in/delasign/",
            },
            publisher: {
              "@type": "Organization",
              name: "delasign",
              logo: {
                "@type": "ImageObject",
                url: "https://delasign.com/delasignWhite.webp",
              },
            },
          })}
        </script>
      );
    } else {
      return "";
    }
  };

  const renderFacebookVerification = () => {
    return (
      <meta
        name="facebook-domain-verification"
        content="8aayqz9viz5ajwifazibz1lfojit65"
      />
    );
  };

  const renderMetaPixel = () => {
    const scripts: any = [];
    scripts.push(
      <script key="function" id="facebook-pixel-script">
        {`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '2805302492934506');
          fbq('track', 'PageView');`}
      </script>
    );
    scripts.push(
      <noscript key="image" id="facebook-pixel-image">{`<img
            height="1"
            width="1"
            style="display:none"
            src="https://www.facebook.com/tr?id=2805302492934506&ev=PageView&noscript=1"
          />`}</noscript>
    );

    return scripts;
  };

  const renderiOSAppBanner = () => {
    if (iOSBanner && iOSBanner.iosBannerAppId != null) {
      // console.log("iOSBanner active  :", iOSBanner);
      switch (iOSBanner.iosAppClipBundleId == null) {
        case true:
          // console.log("No App Clip");
          return (
            <meta
              name="apple-itunes-app"
              content={"app-id=" + iOSBanner.iosBannerAppId}
              data-react-helmet="true"
            ></meta>
          );
        case false:
          // console.log("With App Clip");
          return (
            <meta
              name="apple-itunes-app"
              content={
                "app-id=" +
                iOSBanner.iosBannerAppId +
                ", app-clip-bundle-id=" +
                iOSBanner.iosAppClipBundleId
              }
              data-react-helmet="true"
            ></meta>
          );
      }
    }
  };

  // const renderGoogleAnalytics = () => {
  //   if (process.env.NODE_ENV !== "production") {
  //     return ""
  //   }
  //   // const tags: any = []
  //   // tags.push(<Script
  //   //   src={`https://www.googletagmanager.com/gtag/js?id=${process.env.GATSBY_GA_MEASUREMENT_ID}`}
  //   //   strategy="idle"
  //   //   forward={[`gtag`]}
  //   // />)
  //   // tags.push(<Script
  //   //   id='gtag-config'
  //   //   strategy="idle"
  //   //   forward={[`dataLayer.push`]}
  //   //   dangerouslySetInnerHTML={{
  //   //     __html: `window.dataLayer = window.dataLayer || [];
  //   //     window.gtag = function gtag(){ window.dataLayer.push(arguments);}
  //   //     gtag('js', new Date()); 
  //   //     gtag('config', '${process.env.GATSBY_GA_MEASUREMENT_ID}', { "send_page_view": true, "anonymize_ip": true })`,
  //   //   }}
  //   // />)

  //   const tags: any = []
  //   tags.push(<script
  //     src={`https://www.googletagmanager.com/gtag/js?id=${process.env.GATSBY_GA_MEASUREMENT_ID}`}
  //   />)
  //   tags.push(<script
  //     id='gtag-config'
  //     dangerouslySetInnerHTML={{
  //       __html: `window.dataLayer = window.dataLayer || [];
  //       window.gtag = function gtag(){ window.dataLayer.push(arguments);}
  //       gtag('js', new Date()); 
  //       gtag('config', '${process.env.GATSBY_GA_MEASUREMENT_ID}', { "send_page_view": true, "anonymize_ip": true })`,
  //     }}
  //   />)
  //   return tags
  // }
  
  return (
    <Helmet htmlAttributes={{ lang: "en" }}>
      <link rel="canonical" href={canonical} />
      <meta content="text/html; charset=utf-8" http-equiv="content-type" />
      <meta
        name="robots"
        content={noFollow && noFollow === true ? "noindex" : "INDEX, FOLLOW"}
      />
      <title>{metaTitle}</title>
      <meta name="author" content={pageAuthor.name}></meta>
      <meta name="name" content={metaTitle} />
      <meta name="description" content={metaDescription} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta name="image" property="image" content={seoImage.url} />
      <meta name="image" property="og:image" content={seoImage.url} />
      <meta property="og:image:width" content={seoImage.width.toString()} />
      <meta property="og:image:height" content={seoImage.height.toString()} />
      {renderSiteLinksSearchBox()}
      {renderStructuredDataFAQ()}
      {renderStructuredHowTo()}
      {renderStructuredDataJobPostings()}
      {renderArticleStructuredData()}
      {renderFacebookVerification()}
      {renderMetaPixel()}
      {renderiOSAppBanner()}
    </Helmet>
  );
};

export default MetaData;
